import { useState } from 'react';
import {
  Alert, Badge, Button, Code, SegmentedControl, Stack, Table, Text, TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';

import { useAddSuppression, useDeleteSuppression, useSuppressions } from '../api/hooks';
import { DataState } from '../components/DataState';
import { PageHeader } from '../components/PageHeader';
import { fmtRelative } from '../theme/format';

const REASON_COLOR: Record<string, string> = {
  hard_bounce: 'orange', complaint: 'red', unsubscribe: 'gray', manual: 'blue',
};

export function SuppressionsPage() {
  const [channel, setChannel] = useState('');
  const { data, isLoading, error } = useSuppressions(channel || undefined);
  const add = useAddSuppression();
  const remove = useDeleteSuppression();

  return (
    <>
      <PageHeader
        title="Suppressions"
        description="Addresses that must never be sent to. This is the first gate in the send pipeline and nothing overrides it — not a journey, not a transactional message."
        action={
          <Button
            size="xs" leftSection={<IconPlus size={14} />}
            onClick={() =>
              modals.open({
                title: 'Suppress an address',
                children: (
                  <AddForm
                    onSubmit={(input) =>
                      add.mutate(input, {
                        onSuccess: () => {
                          notifications.show({ message: 'Suppressed', color: 'teal' });
                          modals.closeAll();
                        },
                      })
                    }
                  />
                ),
              })
            }
          >
            Add
          </Button>
        }
      />

      <Stack gap="md">
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Text size="sm">
            Entries appear here automatically: a hard bounce, a spam complaint, or an
            unsubscribe all add one. Removing an entry means mailing someone who asked
            not to be mailed, so every removal is written to the audit log.
          </Text>
        </Alert>

        <SegmentedControl
          size="xs" w="fit-content"
          value={channel}
          onChange={setChannel}
          data={[
            { value: '', label: 'All' },
            { value: 'email', label: 'Email' },
            { value: 'push', label: 'Push' },
            { value: 'sms', label: 'SMS' },
            { value: 'whatsapp', label: 'WhatsApp' },
          ]}
        />

        <DataState
          loading={isLoading}
          error={error}
          empty={!data?.length}
          emptyTitle="Nothing suppressed"
          emptyDescription="A clean suppression list on a project that has been sending is a good sign."
        >
          <Table.ScrollContainer minWidth={620}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Address</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th>Added</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.map((s) => (
                  <Table.Tr key={`${s.channel}-${s.address}`}>
                    <Table.Td><Code>{s.address}</Code></Table.Td>
                    <Table.Td><Badge variant="light" size="sm">{s.channel}</Badge></Table.Td>
                    <Table.Td>
                      <Badge color={REASON_COLOR[s.reason] ?? 'gray'}>{s.reason}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{fmtRelative(s.created_at)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Button
                        size="compact-xs" variant="subtle" color="red"
                        leftSection={<IconTrash size={12} />}
                        onClick={() =>
                          modals.openConfirmModal({
                            title: 'Remove this suppression?',
                            children: (
                              <Text size="sm">
                                {s.reason === 'complaint'
                                  ? 'This address reported a message as spam. Sending to them again is a legal problem in most jurisdictions, not just a deliverability one.'
                                  : 'Sending will resume to this address.'}
                              </Text>
                            ),
                            labels: { confirm: 'Remove', cancel: 'Keep' },
                            confirmProps: { color: 'red' },
                            onConfirm: () =>
                              remove.mutate({ channel: s.channel, address: s.address }),
                          })
                        }
                      >
                        Remove
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </DataState>
      </Stack>
    </>
  );
}

function AddForm({
  onSubmit,
}: {
  onSubmit: (input: { channel: string; address: string; reason: string }) => void;
}) {
  const [channel, setChannel] = useState('email');
  const [address, setAddress] = useState('');

  return (
    <Stack>
      <SegmentedControl
        value={channel}
        onChange={setChannel}
        data={['email', 'push', 'sms', 'whatsapp']}
      />
      <TextInput
        label="Address"
        placeholder="someone@example.com"
        value={address}
        onChange={(e) => setAddress(e.currentTarget.value)}
        data-autofocus
      />
      <Button
        disabled={!address}
        onClick={() => onSubmit({ channel, address, reason: 'manual' })}
      >
        Suppress
      </Button>
    </Stack>
  );
}
